import {type Static, Type} from '@sinclair/typebox'
import {TypeCompiler} from '@sinclair/typebox/compiler'
import {parse} from 'partial-json'

import {getGeoLocation} from '@/lib/locale/geoLocaltion'
import type {SupportedLocales} from '@/lib/locale/locale'
import {type HomePage, transformHomePage} from '@/models/home_deprecated/homePageModel'
import {type SearchResult, transformSearchResult} from '@/models/home_deprecated/searchResultModel'
import {transformFullProducts} from '@/models/productModel'
import call from '@/utils/api/call'
import shouldLogError from '@/utils/api/shouldLogError'
import ParseError from '@/utils/errors/ParseError'

export async function getHomePage({
  signal,
  locale,
}: {
  locale: SupportedLocales
  signal: AbortSignal
}) {
  return call
    .post(
      '/api/model/homepage/',
      {
        localization: {
          geo_location: getGeoLocation(),
          locale,
        },
      },
      {
        signal,
      },
    )
    .then(res => {
      return transformHomePage(res.data)
    })
    .catch(e => {
      if (shouldLogError(e)) {
        console.error('Cannot fetch homepage', e)
      }
      return [] as HomePage
    })
}

export async function getSearchResult({
  q,
  locale,
  signal,
  preload,
  queryKey,
  queryClient,
}: {
  q: string
  locale: SupportedLocales
  signal: AbortSignal
  preload?: boolean
  queryKey: readonly unknown[]
  queryClient: QueryClient
}) {
  return new Promise<SearchResult | false>(resolve => {
    fetch('/api/model/product_search/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        data: {
          query: q,
        },
        localization: {
          geo_location: getGeoLocation(),
          locale,
        },
      }),
      signal,
      priority: preload ? 'low' : 'high',
    })
      .then(res => {
        const readableStream = res.body
        invariant(readableStream instanceof ReadableStream)

        const reader = readableStream.getReader()

        let str_data = ''

        reader
          .read()
          .then(function pump({
            done,
            value,
          }: ReadableStreamReadResult<Uint8Array>): void | Promise<void> {
            const str_value = new TextDecoder().decode(value as AllowSharedBufferSource)
            str_data = str_data.concat(str_value)

            const got = str_data.split('!@#')

            let latestData: SearchResult | undefined

            if (got[got.length - 1] === '') {
              const data = got[got.length - 2]

              const parsedData = data ? (parse(data) as unknown) : undefined

              const transformedData = parsedData ? transformSearchResult(parsedData) : false

              if (transformedData) {
                queryClient.setQueryData(queryKey, transformedData)
                latestData = transformedData
              }
            }

            if (done) {
              resolve(latestData ?? false)
              return
            }
            return reader.read().then(pump)
          })
          .catch(error => {
            throw error
          })
      })
      .catch(e => {
        if (shouldLogError(e)) {
          console.error('Cannot fetch search result', e)
        }
        resolve(false as const)
      })
  })
}

const rawFlashDealSchema = Type.Object({
  products: Type.Array(Type.Object({})),
  message: Type.Optional(Type.String()),
  title: Type.String(),
  timestamp: Type.Number(),
  remaining_seconds: Type.Number(),
})
export type RawFlashDeal = Static<typeof rawFlashDealSchema>
const rawFlashDealTypeCheck = TypeCompiler.Compile(rawFlashDealSchema)
export async function getFlashDeals({
  locale,
  signal,
}: {
  locale: SupportedLocales
  signal: AbortSignal
}) {
  return call
    .post(
      '/api/model/flash_deals/',
      {
        localization: {
          geo_location: getGeoLocation(),
          locale,
        },
      },
      {
        signal,
      },
    )
    .then(res => {
      if (!rawFlashDealTypeCheck.Check(res.data)) {
        console.error('Invalid flash deal data')
        console.log(Array.from(rawFlashDealTypeCheck.Errors(res.data)))
        throw new ParseError(
          'Flash Deal',
          'Invalid flash deal data',
          Array.from(rawFlashDealTypeCheck.Errors(res.data)),
        )
      }
      return {
        products: transformFullProducts(res.data.products),
        message: res.data.message,
        title: res.data.title,
        timestamp: res.data.timestamp,
        remainingSeconds: res.data.remaining_seconds,
      }
    })
    .catch(e => {
      if (shouldLogError(e)) {
        console.error('Cannot fetch flash deals', e)
      }
      return false as const
    })
}
